import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, X, Power } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const euro = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const EMPTY = { name: "", category: "", description: "", reference_price: "", partner_price: "", min_threshold: 15000, stock: "", active: true, image_url: "" };

const ProductModal = ({ product, onClose, onSaved }) => {
  const [form, setForm] = useState(product ? { ...product, stock: product.stock ?? "" } : EMPTY);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim() || !form.reference_price || form.partner_price === "") {
      toast.error("Nom, prix de référence et prix partenaire sont requis"); return;
    }
    setSaving(true);
    const body = {
      name: form.name, category: form.category, description: form.description,
      reference_price: Number(form.reference_price), partner_price: Number(form.partner_price),
      min_threshold: Number(form.min_threshold), stock: form.stock === "" ? null : Number(form.stock),
      active: form.active, image_url: form.image_url,
    };
    try {
      if (product) await api.put(`/phase3/admin/advantage-products/${product.id}`, body);
      else await api.post("/phase3/admin/advantage-products", body);
      toast.success(product ? "Produit mis à jour" : "Produit créé");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/70 p-6 backdrop-blur-sm" onClick={onClose}>
      <div data-testid="product-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-3xl bg-white p-7">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-medium text-navy">{product ? "Modifier le produit" : "Nouveau produit"}</h3>
          <button data-testid="product-modal-close" onClick={onClose} className="rounded-full border border-slate-200 p-2 text-slate-500 hover:bg-navy hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="mt-5 max-h-[60vh] space-y-3 overflow-y-auto pr-1">
          <input data-testid="product-name" placeholder="Nom du produit" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
          <input data-testid="product-category" placeholder="Catégorie" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="product-ref-price" type="number" placeholder="Valeur / prix référence" value={form.reference_price}
              onChange={(e) => setForm({ ...form, reference_price: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
            <input data-testid="product-partner-price" type="number" placeholder="Votre prix (partenaire)" value={form.partner_price}
              onChange={(e) => setForm({ ...form, partner_price: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
          </div>
          <select data-testid="product-threshold" value={form.min_threshold} onChange={(e) => setForm({ ...form, min_threshold: e.target.value })}
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none">
            <option value={15000}>Palier minimum : 15 000 €</option>
            <option value={25000}>Palier minimum : 25 000 €</option>
          </select>
          <input data-testid="product-stock" type="number" placeholder="Stock (vide = illimité)" value={form.stock}
            onChange={(e) => setForm({ ...form, stock: e.target.value })}
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
          <input data-testid="product-image" placeholder="URL image" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })}
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
          <textarea data-testid="product-description" placeholder="Description" rows={2} value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
        </div>
        <button data-testid="product-submit" disabled={saving} onClick={submit}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-navy px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-cognac disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {product ? "Enregistrer" : "Créer le produit"}
        </button>
      </div>
    </div>
  );
};

export const AdvantageCatalog = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const fetchProducts = useCallback(() => {
    api.get("/phase3/advantage-products")
      .then((res) => setProducts(res.data))
      .catch(() => toast.error("Impossible de charger le catalogue"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const toggle = async (product) => {
    try {
      await api.patch(`/phase3/admin/advantage-products/${product.id}/toggle`);
      toast.success(product.active ? "Produit désactivé" : "Produit activé");
      fetchProducts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-navy" /></div>;

  return (
    <div data-testid="advantage-catalog">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Catalogue « Avantages CozyHome » — visible par les partenaires selon leur palier.</p>
        <button data-testid="catalog-new-product" onClick={() => setShowNew(true)}
          className="flex items-center gap-1.5 rounded-full bg-navy px-4 py-1.5 text-xs font-bold text-white transition-colors hover:bg-cognac">
          <Plus className="h-3.5 w-3.5" /> Nouveau produit
        </button>
      </div>

      <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((p) => (
          <div key={p.id} data-testid={`catalog-product-${p.id}`} className={`overflow-hidden rounded-2xl border border-slate-200 bg-white ${!p.active ? "opacity-50" : ""}`}>
            {p.image_url && <img src={p.image_url} alt={p.name} className="h-40 w-full object-cover" />}
            <div className="p-5">
              <p className="text-[11px] font-bold uppercase tracking-wider text-cognac">{p.category || "Matériel"}</p>
              <p className="mt-1 font-display text-lg font-medium text-navy">{p.name}</p>
              <p className="mt-2 text-sm text-slate-500">Valeur : {euro(p.reference_price)}</p>
              <p className="text-sm text-slate-500">Avantage CozyHome : {euro(p.cozyhome_discount)}</p>
              <p className="text-sm font-bold text-navy">Prix partenaire : {euro(p.partner_price)}</p>
              <p className="mt-2 text-xs text-slate-400">Disponible à partir de {euro(p.min_threshold)}{p.stock !== null && p.stock !== undefined ? ` — stock : ${p.stock}` : ""}</p>
              <div className="mt-4 flex gap-2">
                <button data-testid={`catalog-edit-${p.id}`} onClick={() => setEditing(p)}
                  className="flex-1 rounded-full border border-navy/20 px-3 py-2 text-xs font-bold text-navy transition-colors hover:border-cognac hover:text-cognac">
                  Modifier
                </button>
                <button data-testid={`catalog-toggle-${p.id}`} onClick={() => toggle(p)}
                  className="flex items-center gap-1 rounded-full border border-slate-200 px-3 py-2 text-xs font-bold text-slate-500 transition-colors hover:border-red-300 hover:text-red-500">
                  <Power className="h-3 w-3" /> {p.active ? "Désactiver" : "Activer"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {(editing || showNew) && (
        <ProductModal product={editing} onClose={() => { setEditing(null); setShowNew(false); }} onSaved={() => { setEditing(null); setShowNew(false); fetchProducts(); }} />
      )}
    </div>
  );
};
